/**
 * Probiotic "Learn More" page behaviour.
 *
 * The long-form content is presented as accordion cards on every breakpoint.
 * Below MOBILE_BREAKPOINT the intro copy is additionally clamped behind a
 * "Read more" toggle; above it the intro is shown in full (see the Figma
 * desktop design).
 *
 * Markup lives in snippets/updated-learn-more-page-content.liquid,
 * styles in assets/probiotic-learn-more.css.
 */
(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 768;
  const mobile = window.matchMedia('(max-width: ' + MOBILE_BREAKPOINT + 'px)');

  function LearnMore(root) {
    this.root = root;
    this.items = Array.prototype.slice.call(root.querySelectorAll('.lm-acc__item'));
    this.intro = root.querySelector('.lm-intro');
    this.clamp = root.querySelector('[data-lm-clamp]');
    this.clampFrom = root.querySelector('[data-lm-clamp-from]');
    this.rest = root.querySelector('.lm-intro__text-rest');
    this.moreButton = root.querySelector('.lm-intro__more-button');
    this.moreLabel = root.querySelector('.lm-intro__more-label');
  }

  /* ---------------------------------------------------------------- accordions */

  LearnMore.prototype.setItem = function (item, open) {
    const toggle = item.querySelector('.lm-acc__toggle');
    item.classList.toggle('is-open', open);
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  LearnMore.prototype.bindAccordions = function () {
    const self = this;
    this.items.forEach(function (item) {
      const toggle = item.querySelector('.lm-acc__toggle');
      if (!toggle) return;
      toggle.addEventListener('click', function () {
        self.setItem(item, !item.classList.contains('is-open'));
      });
      self.setItem(item, false);
    });
  };

  /* -------------------------------------------------------------- intro clamp */

  /**
   * Height that shows the intro copy down to the first two lines of the
   * paragraph marked with `data-lm-clamp-from`, so the gradient always fades
   * out mid-paragraph as designed instead of on an arbitrary pixel value.
   */
  LearnMore.prototype.clampHeight = function () {
    if (!this.clamp || !this.clampFrom) return null;

    let lineHeight = parseFloat(getComputedStyle(this.clampFrom).lineHeight);
    if (!lineHeight) lineHeight = 26;

    // Walk the offsetParent chain so the offset is measured against the clamp
    // container itself, whatever it happens to be positioned relative to.
    let top = 0;
    let el = this.clampFrom;
    while (el && el !== this.clamp) {
      top += el.offsetTop;
      el = el.offsetParent;
    }
    if (!el) return null;

    return Math.round(top + lineHeight * 2);
  };

  LearnMore.prototype.setIntro = function (open) {
    if (!this.clamp) return;

    this.clamp.classList.toggle('is-open', open);
    this.clamp.style.maxHeight = open ? '' : (this.clampHeight() || 290) + 'px';

    if (this.rest) this.rest.hidden = !open;

    if (this.moreButton) this.moreButton.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (this.moreLabel) {
      this.moreLabel.textContent = open
        ? this.moreLabel.getAttribute('data-label-less')
        : this.moreLabel.getAttribute('data-label-more');
    }
  };

  /** Desktop: no clamp, no toggle - the whole intro is on the page. */
  LearnMore.prototype.releaseIntro = function () {
    if (this.clamp) {
      this.clamp.classList.remove('is-open');
      this.clamp.style.maxHeight = '';
    }
    if (this.rest) this.rest.hidden = false;
    if (this.moreButton) this.moreButton.setAttribute('aria-expanded', 'false');
  };

  LearnMore.prototype.bindIntro = function () {
    const self = this;
    if (!this.moreButton) return;
    this.moreButton.addEventListener('click', function () {
      self.setIntro(!self.clamp.classList.contains('is-open'));
    });
  };

  /** Re-clamp after a reflow, but only while the clamp is actually collapsed. */
  LearnMore.prototype.reclamp = function () {
    if (mobile.matches && this.clamp && !this.clamp.classList.contains('is-open')) {
      this.setIntro(false);
    }
  };

  LearnMore.prototype.syncIntro = function () {
    if (mobile.matches) this.setIntro(false);
    else this.releaseIntro();
  };

  function init() {
    const root = document.querySelector('.updated-learn-more');
    if (!root) return;

    const page = new LearnMore(root);
    page.bindAccordions();
    page.bindIntro();
    page.syncIntro();

    const onChange = page.syncIntro.bind(page);
    if (mobile.addEventListener) mobile.addEventListener('change', onChange);
    else mobile.addListener(onChange);

    // The clamp height depends on how the intro copy wraps, so recompute it
    // when the viewport width changes or a webfont finishes loading.
    let resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(page.reclamp.bind(page), 150);
    });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(page.reclamp.bind(page));
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
